import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import Limiter from "../../../../../components/atoms/Limiter";
import ExpandLink from "../../../../../components/atoms/Limiter/ExpandLink";
import TableSorter from "../../../../../components/atoms/TableSorter";
import AACTTable from "./AACTTable";
import { sortArrOfObjs } from "../../../../../common/helpers/helpers";
import helpers from "../../../../../common/utils/utils";

const sortByStartDate = (a, b) => {
  return new Date(b.start_date) - new Date(a.start_date);
};

const renderTable = (props) => {
  return <AACTTable {...props} />;
};

const renderLimiter = (props) => {
  return (
    <div className="varsome-inner-container">
      <Limiter
        {...props}
        ExpandLink={(p) => (
          <div className="tw-p-2">
            <ExpandLink {...p} />
          </div>
        )}
        items={props.sortedItems}
        limit={5}
        renderContent={renderTable}
      />
    </div>
  );
};

const formatDates = (date) => {
  const res = date.split("date:");
  const formattedDate = res && res[1] && res[1].trim().replace(/-/g, "");
  return helpers.parseNumericDate(`${formattedDate}`);
};

const compare = (a, b) => {
  if ((a.drug || a.name) > (b.drug || b.name)) return 1;
  if ((a.drug || a.name) < (b.drug || b.name)) return -1;
  return 0;
};

function AACTContent(props) {
  const { aactList } = props;
  const [formattedItemsList, setFormattedItemsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const search$ = useRef(new Subject());

  const handleChange = (event) => {
    search$.current.next(event.target.value);
  };

  useEffect(() => {
    const items = aactList.map((x) => {
      return {
        ...x,
        start_date: x.start_date ? formatDates(x.start_date) : "",
        completion_date: x.completion_date
          ? formatDates(x.completion_date)
          : "",
        phenotypes: x.phenotypes ? x.phenotypes.sort() : [],
        conditions: x.conditions ? x.conditions.sort() : [],
        facilities: x.facilities
          ? sortArrOfObjs(x.facilities, "country", "asc")
          : [],
        interventions: x.interventions ? x.interventions.sort(compare) : [],
      };
    });
    setFormattedItemsList(items);
  }, []);

  useEffect(() => {
    const subscription = search$.current
      .pipe(debounceTime(500))
      .subscribe((x) => setSearchTerm(x));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const escaped = searchTerm.replace(/[-[/\]{}()*+?.,\\^$|#\s]/g, "\\$&");
    const reg = new RegExp(escaped, "gi");
    const results = formattedItemsList.filter(
      (field) =>
        field.status.match(reg) ||
        JSON.stringify(field.facilities).match(reg) ||
        JSON.stringify(field.phenotypes).match(reg) ||
        JSON.stringify(field.conditions).match(reg) ||
        JSON.stringify(field.interventions).match(reg) ||
        field.start_date.match(reg) ||
        field.completion_date.match(reg)
    );
    setSearchResults(results);
  }, [searchTerm, formattedItemsList]);

  return (
    <React.Fragment>
      <div className="tw-p-5 tw-inline-block" data-testid="aact-gene-component">
        <div className="tw-text-coolGray-400 tw-text-xs tw-flex tw-items-center">
          <div className="tw-mr-1">
            SEARCH FOR STATUS, DATES, CONDITIONS, PHENOTYPES, INTERVENTIONS OR
            LOCATIONS:
          </div>
        </div>
        <div className="tw-py-2" style={{ width: "400px" }}>
          <input
            type="text"
            className="form-control tw-mb-2"
            placeholder="status, date, conditions, phenotypes, interventions or locations"
            onChange={handleChange}
          />
        </div>
      </div>
      <TableSorter
        {...props}
        items={searchResults}
        defaultSortedField="start_date"
        defaultComparator={sortByStartDate}
        renderContent={renderLimiter}
      />
    </React.Fragment>
  );
}

AACTContent.propTypes = {
  aactList: PropTypes.arrayOf(
    PropTypes.shape({
      calculated_values: PropTypes.arrayOf(PropTypes.shape({})),
      conditions: PropTypes.arrayOf(PropTypes.string),
      designs: PropTypes.arrayOf(PropTypes.shape({})),
      eligibilities: PropTypes.arrayOf(PropTypes.shape({})),
      facilities: PropTypes.arrayOf(PropTypes.shape({})),
      genes: PropTypes.arrayOf(PropTypes.string),
      interventions: PropTypes.arrayOf(PropTypes.shape({})),
      is_fda_drug: PropTypes.bool,
      nct_id: PropTypes.string,
      outcomes: PropTypes.arrayOf(
        PropTypes.shape({
          description: PropTypes.string,
          outcome_type: PropTypes.string,
          param_type: PropTypes.string,
          population: PropTypes.string,
          time_frame: PropTypes.string,
          title: PropTypes.string,
          units: PropTypes.string,
        })
      ),
      overall_officials: PropTypes.arrayOf(PropTypes.shape({})),
      phenotypes: PropTypes.arrayOf(PropTypes.string),
      population: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      pub_med_references: PropTypes.arrayOf(PropTypes.any),
      source: PropTypes.string,
      sponsors: PropTypes.arrayOf(PropTypes.shape({})),
      status: PropTypes.string,
      title: PropTypes.string,
      unidentified_keywords: PropTypes.arrayOf(PropTypes.string),
    })
  ),
};

AACTContent.defaultProps = {
  aactList: [],
};

export default AACTContent;
